import {
  column,
  GridColumn,
  GridSortConfig,
  GridTableLayout,
  numericColumn,
  simpleDataRows,
  simpleHeader,
  SimpleHeaderAndData,
  useGridTableLayoutState,
} from "@homebound/beam";
import { useCallback, useMemo, useState } from "react";
import { priceCell } from "src/components";
import { CostConfigurationSummaryOrderBy, Order } from "src/generated/graphqlTypes";
import { useProjectContext } from "src/routes/projects/context/ProjectContext";
import { parseOrder, toOrder } from "src/utils/ordering";
import {
  AllConfigurations_CostConfigurationSummaryFragment,
  AllConfigurations_PotentialBidPlanDimensionResultFragment,
  AllConfigurationsQuery,
  useAllConfigurationsDimensionsQuery,
  useAllConfigurationsQuery,
} from "./AllConfigurationsTab.generated";
import {
  ALL_CONFIGURATIONS_PAGE_SIZE,
  AllConfigurationsFilter,
  createFilterDefs,
  mapToFilter,
  typeDimensions,
} from "./utils";

type AllConfigurationsTabProps = { productOfferings: { id: string; name: string }[] };

export function AllConfigurationsTab({ productOfferings }: AllConfigurationsTabProps) {
  const { id: projectId } = useProjectContext();
  const productOfferingIds = useMemo(() => productOfferings.map((po) => po.id), [productOfferings]);
  const dimensionsQuery = useAllConfigurationsDimensionsQuery({
    variables: { productOfferingIds },
    skip: productOfferingIds.isEmpty,
  });
  const dimensions = useMemo(() => dimensionsQuery.data?.potentialBidPlanDimensions ?? [], [dimensionsQuery.data]);
  const filterDefs = useMemo(() => createFilterDefs(productOfferings, dimensions), [productOfferings, dimensions]);
  const columns = useMemo(() => createColumns(dimensions), [dimensions]);
  const layoutState = useGridTableLayoutState<AllConfigurationsFilter>({
    persistedFilter: { filterDefs, storageKey: `${projectId}_costAnalysisAllConfigurationsFilter` },
    search: "server",
  });
  const [orderBy, setOrderBy] = useState<CostConfigurationSummaryOrderBy>(defaultAllConfigurationsOrderBy);
  const query = useAllConfigurationsQuery({
    variables: {
      filter: mapToFilter(layoutState.filter, layoutState.searchString, productOfferingIds),
      orderBy,
      page: { offset: 0, limit: ALL_CONFIGURATIONS_PAGE_SIZE },
    },
    skip: productOfferingIds.isEmpty,
  });
  const sorting: GridSortConfig = useMemo(
    () => ({
      on: "server",
      onSort: (key, direction) => setOrderBy(toOrder(key, direction)),
      value: parseOrder(orderBy),
    }),
    [orderBy],
  );
  const createRows = useCallback(
    (data: AllConfigurationsQuery | undefined) => [
      simpleHeader,
      ...simpleDataRows(data?.costConfigurationSummaries.entities),
    ],
    [],
  );
  const maybeFetchNextPage = useCallback(async () => {
    const page = query.data?.costConfigurationSummaries;
    if (page?.pageInfo.hasNextPage) {
      await query.fetchMore({
        variables: { page: { offset: page.entities.length, limit: ALL_CONFIGURATIONS_PAGE_SIZE } },
      });
    }
  }, [query]);
  return (
    <GridTableLayout
      layoutState={layoutState}
      emptyFallback="There aren't any configurations to display."
      tableProps={{
        query,
        createRows,
        columns,
        sorting,
        as: "virtual",
        infiniteScroll: { onEndReached: maybeFetchNextPage },
      }}
    />
  );
}

export const defaultAllConfigurationsOrderBy: CostConfigurationSummaryOrderBy = { productOffering: Order.Asc };

type Row = SimpleHeaderAndData<AllConfigurations_CostConfigurationSummaryFragment>;

function createColumns(dimensions: AllConfigurations_PotentialBidPlanDimensionResultFragment[]): GridColumn<Row>[] {
  return [
    column<Row>({
      id: "productOffering",
      name: "Product Offering",
      header: "Product Offering",
      serverSideSortKey: "productOffering",
      canHide: false,
      data: ({ productOffering }) => productOffering.name,
      mw: "200px",
    }),
    ...typeDimensions(dimensions).map(({ type, displayName }) =>
      column<Row>({
        id: type.id,
        name: displayName,
        header: displayName,
        canHide: false,
        data: ({ pinnedOptions }) => pinnedOptions.find((rpo) => rpo.type.id === type.id)?.name,
      }),
    ),
    numericColumn<Row>({
      id: "totalCost",
      name: "Total Cost",
      header: "Total Cost",
      serverSideSortKey: "totalCostInCents",
      canHide: true,
      data: ({ totalCostInCents }) => priceCell({ valueInCents: totalCostInCents }),
    }),
    numericColumn<Row>({
      id: "totalPrice",
      name: "Total Price",
      header: "Total Price",
      serverSideSortKey: "totalPriceInCents",
      canHide: true,
      initHidden: true,
      data: ({ totalPriceInCents }) => priceCell({ valueInCents: totalPriceInCents }),
    }),
  ];
}
